import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Check, PanelLeftClose, PanelLeftOpen, Maximize, Minimize, MessageCircle, Send, Trash2, Users } from 'lucide-react'
import { collaborationService, type GroupTask } from '../../../services/collaboration/collaborationService'
import { API_CONFIG } from '../../../config/api.config'
import { lessonApi, resultApi, channelApi, type LessonQuestion } from '../api/lessonBuilderApi'
import LessonCardRenderer from '../components/LessonCardRenderer'
import QuestSection from '../components/LearningPath'
import { normalizeLesson, sectionKindMeta } from '../lib/sections'
import { isViewableTextBlock } from '../../../components/interactive/shared/contentBlocks'
import { LessonStatsService } from '../../../services/stats/LessonStatsService'
import { LessonDocProvider } from '../lib/lessonLive'
import { useAuth } from '../../../contexts/AuthContext'
import type { LessonDoc } from '../types/LessonDoc'

function isInteractiveVideoCard(card: any): boolean {
  if (!card || !Array.isArray(card.checkpoints) || card.checkpoints.length === 0) return false
  if (card.type === 'video') return !!(card.youtubeId || card.src)
  if (card.type === 'pptx') return !!card.recordingUrl
  return false
}

function cardDoneKey(cardId: string): string {
  return `${cardId}:__done__`
}

// Trình xem bài giảng kiểu e-learning: thanh tab các PHẦN bên trái + khu nội dung rộng,
// xem từng phần một, có thanh tiến độ và nút Phần trước/sau. Render bằng LessonCardRenderer
// (cùng đường render với builder → "xem trước = xuất bản").
const LessonViewerPage: React.FC = () => {
  const { slug } = useParams()
  const { user } = useAuth()
  const bypassLock = user?.role === 'teacher' || user?.role === 'admin'  // GV/Admin xem preview, không bị khoá ải
  const [lesson, setLesson] = useState<LessonDoc | null | undefined>(undefined)
  const [active, setActive] = useState(0)
  const [visited, setVisited] = useState<Set<number>>(new Set([0]))
  const [navOpen, setNavOpen] = useState(true)   // ẩn/hiện thanh tab các phần
  const [isFs, setIsFs] = useState(false)         // toàn màn hình (projector)
  const [, setStatsTick] = useState(0)            // nhịp làm tươi % tiến độ (service mutate ngoài React)
  const [restored, setRestored] = useState(false) // đã nạp xong tiến độ từ backend chưa (gate render nội dung)
  const [quizStatus, setQuizStatus] = useState<Record<string, { done: boolean; percent: number | null }>>({}) // trắc nghiệm PMaker HS đã làm
  const [groupTasks, setGroupTasks] = useState<GroupTask[]>([])   // nhiệm vụ nhóm gắn vào bài này
  const [questions, setQuestions] = useState<LessonQuestion[]>([])
  const [questionText, setQuestionText] = useState('')
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string>>({})
  const [postingQuestion, setPostingQuestion] = useState(false)
  const restoredFlagRef = useRef(false)           // chặn gửi/ghi đè backend TRƯỚC khi khôi phục xong
  const saveTimerRef = useRef<number | null>(null)

  // Đồng bộ trạng thái fullscreen với trình duyệt
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {})
      setNavOpen(false)  // vào toàn màn hình → ẩn tab để nội dung rộng tối đa
    } else {
      document.exitFullscreen?.().catch(() => {})
    }
  }

  useEffect(() => {
    if (slug) {
      lessonApi
        .getBySlug(slug)
        .then((c) => setLesson(c ? normalizeLesson(c) : null))
        .catch(() => setLesson(null))
    }
  }, [slug])

  // Chỉ điều hướng giữa các phần CÓ nội dung.
  const sections = useMemo(
    () => (lesson?.sections || []).filter((s) => s.cards.length > 0),
    [lesson]
  )
  const statsKey = lesson ? `bai-giang:${lesson.slug || lesson.id}` : ''

  // Kẹp chỉ số active trong khoảng hợp lệ + đánh dấu đã xem.
  useEffect(() => {
    if (active > sections.length - 1) setActive(Math.max(0, sections.length - 1))
  }, [sections.length, active])

  const goto = (i: number) => {
    const idx = Math.max(0, Math.min(sections.length - 1, i))
    setActive(idx)
    setVisited((prev) => new Set(prev).add(idx))
    // Cuộn về đầu do effect [active] đảm nhiệm (tức thời + tái khẳng định) để luôn bắt đầu từ nội dung đầu tiên.
  }

  // Vào phần MỚI → luôn bắt đầu từ ĐẦU nội dung. Tái khẳng định sau 1 nhịp để chống
  // hiện tượng trang tự "neo cuộn" xuống khi ảnh/iframe mô phỏng tải bất đồng bộ.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
    const t1 = window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 120)
    const t2 = window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 450)
    return () => { window.clearTimeout(t1); window.clearTimeout(t2) }
  }, [active])

  // ── Ghi tiến độ học tập (/study/progress) để bài giảng e-learning vào thống kê GV ──
  const lastTickRef = useRef<number>(0)
  const lessonRef = useRef<LessonDoc | null>(null)
  const prevVisitedRef = useRef<number>(-1)
  const submittedRef = useRef<boolean>(false)
  const visitedRef = useRef<Set<number>>(new Set([0]))
  const totalRef = useRef<number>(0)
  useEffect(() => { lessonRef.current = lesson || null }, [lesson])
  useEffect(() => {
    visitedRef.current = visited
    totalRef.current = sections.length
  }, [visited, sections.length])

  // Mục tính tiến độ mỗi phần: KHỐI TEXT (đọc), CÂU HỎI tương tác (trả lời), VIDEO (xem).
  const GATE_TYPES = useMemo(() => new Set(['interactive_question', 'multiple_response_question', 'sequence_question']), [])
  const gateTotals = useMemo(
    () => sections.map((s) =>
      s.cards.reduce((n, c: any) =>
        n + (c.type === 'rich' ? (c.items || []).filter((it: any) => it && GATE_TYPES.has(it.type)).length : 0), 0)),
    [sections, GATE_TYPES]
  )
  // Tổng khối text đọc-được TRONG các thẻ rich (khớp nơi InteractiveContentRenderer ghi "đã xem khối").
  const textTotals = useMemo(
    () => sections.map((s) =>
      s.cards.reduce((n, c: any) =>
        n + (c.type === 'rich' ? (c.items || []).filter((it: any) => isViewableTextBlock(it)).length : 0), 0)),
    [sections]
  )
  const interactiveVideoIds = useMemo(
    () => sections.map((s) => s.cards.filter(isInteractiveVideoCard).map((c: any) => c.id as string)),
    [sections]
  )
  const videoTotals = useMemo(() => interactiveVideoIds.map((ids) => ids.length), [interactiveVideoIds])
  // Trắc nghiệm PMaker (thẻ 'quiz', bankRef = slug đề) → tính vào tiến độ phần khi HS ĐÃ LÀM (ExamAttempt).
  const quizRefs = useMemo(() => sections.map((s) => s.cards.filter((c: any) => c.type === 'quiz' && c.bankRef).map((c: any) => c.bankRef as string)), [sections])
  const quizTotals = useMemo(() => quizRefs.map((r) => r.length), [quizRefs])
  const quizDone = useMemo(() => quizRefs.map((refs) => refs.filter((r) => quizStatus[r]?.done).length), [quizRefs, quizStatus])
  const gateTotalsRef = useRef<number[]>([])
  const textTotalsRef = useRef<number[]>([])
  const videoTotalsRef = useRef<number[]>([])
  const quizTotalsRef = useRef<number[]>([])
  const quizDoneRef = useRef<number[]>([])
  const sectionTitlesRef = useRef<string[]>([])
  useEffect(() => {
    gateTotalsRef.current = gateTotals
    textTotalsRef.current = textTotals
    videoTotalsRef.current = videoTotals
    quizTotalsRef.current = quizTotals
    quizDoneRef.current = quizDone
    sectionTitlesRef.current = sections.map((s) => s.title || '')
  }, [gateTotals, textTotals, videoTotals, quizTotals, quizDone, sections])

  // Tỉ lệ hoàn thành 1 phần = (khối text đã xem + câu hỏi đã trả lời + video đã xem) / tổng các mục đó.
  // Phần không có mục nào → coi xong khi đã xem. Đọc trực tiếp LessonStatsService (mutate ngoài React).
  const sectionRatioFrom = (i: number, b: any, viewed: boolean): number => {
    const tT = textTotalsRef.current[i] || 0
    const qT = gateTotalsRef.current[i] || 0
    const vT = videoTotalsRef.current[i] || 0
    const zT = quizTotalsRef.current[i] || 0
    const total = tT + qT + vT + zT
    if (total === 0) return viewed ? 100 : 0
    const tD = Math.min(tT, b?.textViewed?.size || 0)
    const qD = Math.min(qT, (b?.correct || 0) + (b?.wrong || 0))
    const doneKeys = statsKey ? LessonStatsService.getAnsweredKeys(statsKey) : new Set<string>()
    const vD = Math.min(vT, (interactiveVideoIds[i] || []).filter((id) => doneKeys.has(cardDoneKey(id))).length)
    const zD = Math.min(zT, quizDoneRef.current[i] || 0)   // trắc nghiệm PMaker HS đã làm
    return Math.round(((tD + qD + vD + zD) / total) * 100)
  }
  const computeDone = (): { done: number; perSection: any[] } => {
    const l = lessonRef.current
    const key = l ? `bai-giang:${l.slug || l.id}` : ''
    const st = key ? LessonStatsService.getStats(key) : null
    const perSection: any[] = []
    let done = 0
    for (let i = 0; i < totalRef.current; i++) {
      const b = st?.sections?.[i]
      const viewed = visitedRef.current.has(i)
      const totalQ = gateTotalsRef.current[i] || 0
      const ratio = sectionRatioFrom(i, b, viewed)
      const isDone = ratio >= 100
      if (isDone) done++
      perSection.push({
        idx: i,
        title: sectionTitlesRef.current[i] || b?.title || `Phần ${i + 1}`,
        viewed,
        ratio,
        totalQuestions: totalQ,
        correct: b?.correct || 0,
        wrong: b?.wrong || 0,
        hints: b?.hints || 0,
        ai: b?.ai || 0,
        textViewed: Math.min(textTotalsRef.current[i] || 0, b?.textViewed?.size || 0),
        textTotal: textTotalsRef.current[i] || 0,
        done: isDone,
      })
    }
    return { done, perSection }
  }

  const authToken = () =>
    localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''

  // Gửi 1 lần: time_spent là DELTA (backend cộng dồn); progress/status ghi đè.
  const sendProgress = (deltaSec: number) => {
    const l = lessonRef.current
    const tk = authToken()
    // KHÔNG gửi trước khi khôi phục xong → tránh ghi đè tiến độ đã lưu bằng dữ liệu rỗng.
    if (!l || !tk || !restoredFlagRef.current) return
    const key = `bai-giang:${l.slug || l.id}`
    // % hoàn thành theo phần ĐÃ HỌC (xem + trả lời hết câu hỏi chốt), không chỉ đã mở.
    const { done } = computeDone()
    const total = totalRef.current || 0
    const doneCapped = total ? Math.min(done, total) : done   // không vượt tổng (tránh 5/4, 125%)
    const pct = total ? Math.min(100, Math.round((doneCapped / total) * 100)) : 0
    const status = pct >= 100 ? 'completed' : 'in_progress'
    const body = {
      lesson_id: key,
      lesson_title: l.meta?.tieuDe || 'Bài giảng',
      progress: pct,
      status,
      time_spent: Math.max(0, Math.min(deltaSec, 600)), // chặn đếm thời gian tab bỏ ngỏ
      // % nội dung = số PHẦN ĐÃ HỌC / tổng phần (cho /study/progress/stats) + STATE để khôi phục đầy đủ
      meta_data: {
        completed_count: doneCapped,
        total_count: total,
        state: { stats: LessonStatsService.serialize(key), visited: Array.from(visitedRef.current) },
      },
    }
    try {
      fetch(`${API_CONFIG.python.apiUrl}/study/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {})
    } catch { /* noop */ }
  }

  const flushProgress = () => {
    if (!lastTickRef.current) return
    const delta = Math.floor((Date.now() - lastTickRef.current) / 1000)
    lastTickRef.current = Date.now()
    sendProgress(delta)
  }

  const completeStandaloneCard = (card: any) => {
    if (!statsKey || !isInteractiveVideoCard(card)) return
    const doneKey = cardDoneKey(card.id)
    const answered = LessonStatsService.getAnsweredKeys(statsKey)
    if (answered.has(doneKey)) return
    LessonStatsService.recordAnswered(doneKey, statsKey)
    LessonStatsService.recordVideoWatched(statsKey)
    setStatsTick((t) => t + 1)
    flushProgress()
  }

  // Nộp kết quả câu hỏi chốt (tổng hợp từ LessonStatsService) về backend — 1 lần/phiên xem.
  const submitResults = () => {
    const l = lessonRef.current
    const tk = authToken()
    if (!l || !tk || submittedRef.current) return
    const key = `bai-giang:${l.slug || l.id}`
    const st = LessonStatsService.getStats(key)
    const answered = st ? st.correctAnswers + st.wrongAnswers : 0
    // Có tương tác câu hỏi HOẶC đã xem hết nội dung → mới gửi (tránh ghi rỗng).
    if (answered <= 0 && visitedRef.current.size < totalRef.current) return
    submittedRef.current = true
    const { done, perSection } = computeDone()
    const contentPercent = totalRef.current ? Math.round((done / totalRef.current) * 100) : 0
    resultApi.submit(l.id, {
      lesson_slug: l.slug,
      total: answered,
      correct: st?.correctAnswers || 0,
      score: st?.correctAnswers || 0,
      max_score: answered,
      // Chi tiết tương tác cho GV: đúng/sai, gợi ý, hỏi AI, video, breakdown TỪNG PHẦN.
      detail: {
        correct: st?.correctAnswers || 0,
        wrong: st?.wrongAnswers || 0,
        hintsUsed: st?.hintsUsed || 0,
        aiAsked: st?.aiQuestionsAsked || 0,
        videoWatched: st?.videoWatched || 0,
        sectionsViewed: visitedRef.current.size,
        sectionsDone: done,
        totalSections: totalRef.current,
        contentPercent,
        sections: perSection,
      },
    })
  }

  // Vào bài: KHÔI PHỤC tiến độ đã lưu (backend, theo TÀI KHOẢN) → hydrate trước khi render nội dung;
  // sau đó theo dõi + tự lưu định kỳ + flush khi rời/ẩn trang. KHÔNG resetStats (giữ kết quả đã học).
  useEffect(() => {
    if (!lesson || sections.length === 0) return
    const key = `bai-giang:${lesson.slug || lesson.id}`
    const tk = authToken()
    let cancelled = false
    setRestored(false)
    restoredFlagRef.current = false
    submittedRef.current = false

    const init = async () => {
      if (tk) {
        try {
          const res = await fetch(
            `${API_CONFIG.python.apiUrl}/study/progress?lesson_id=${encodeURIComponent(key)}`,
            { headers: { Authorization: `Bearer ${tk}` } }
          )
          if (res.ok) {
            const arr = await res.json()
            const rec = Array.isArray(arr) ? (arr.find((p: any) => p.lesson_id === key) || arr[0]) : null
            const st = rec?.meta_data?.state
            if (st?.stats) LessonStatsService.hydrate(key, st.stats)
            if (Array.isArray(st?.visited) && st.visited.length) {
              const vs = new Set<number>(st.visited as number[])
              setVisited(vs); visitedRef.current = vs
            }
          }
        } catch { /* không có/không tải được tiến độ → bắt đầu mới */ }
      }
      if (cancelled) return
      // startLesson KHÔNG xoá dữ liệu đã hydrate (chỉ tạo mới khi chưa có).
      LessonStatsService.startLesson(key)
      LessonStatsService.setCurrentSection(0, sections[0]?.title)
      // Không re-record video cho phần ĐÃ XEM (tránh đếm trùng sau khôi phục).
      lastTickRef.current = Date.now()
      restoredFlagRef.current = true
      setRestored(true)
      sendProgress(0)
      // Tự lưu định kỳ để kết quả không mất dù không rời trang.
      saveTimerRef.current = window.setInterval(() => flushProgress(), 20000)
    }
    init()

    const onLeave = () => { flushProgress(); submitResults() }
    const onVis = () => { if (document.hidden) flushProgress() }
    window.addEventListener('beforeunload', onLeave)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      cancelled = true
      if (saveTimerRef.current) { window.clearInterval(saveTimerRef.current); saveTimerRef.current = null }
      window.removeEventListener('beforeunload', onLeave)
      document.removeEventListener('visibilitychange', onVis)
      onLeave()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, sections.length])

  // Mỗi lần xem thêm 1 phần → ghi nhận thời gian + % cập nhật.
  useEffect(() => {
    if (prevVisitedRef.current >= 0 && visited.size !== prevVisitedRef.current && lastTickRef.current) {
      flushProgress()
    }
    prevVisitedRef.current = visited.size
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visited])

  // Làm tươi % tiến độ trên thanh điều hướng khi HS cuộn xem khối / trả lời (service mutate ngoài React).
  useEffect(() => {
    if (!lesson || sections.length === 0) return
    const id = window.setInterval(() => setStatsTick((t) => t + 1), 1500)
    return () => window.clearInterval(id)
  }, [lesson?.id, sections.length])

  // Chuyển phần → đặt phần hiện tại cho thống kê + ghi nhận video của phần (1 lần/phần).
  useEffect(() => {
    if (!lesson || sections.length === 0) return
    LessonStatsService.setCurrentSection(active, sections[active]?.title)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, lesson?.id, sections.length])

  // Nhiệm vụ NHÓM gắn vào bài này (hiện banner dẫn sang /cong-tac)
  useEffect(() => {
    if (!lesson?.id) return
    collaborationService.tasksByLesson(lesson.id).then(setGroupTasks).catch(() => { /* noop */ })
  }, [lesson?.id])

  // Trắc nghiệm PMaker: nạp trạng thái làm bài của HS + LÀM TƯƠI khi quay lại tab (sau khi làm đề ở /exam).
  useEffect(() => {
    if (!lesson?.id) return
    const load = () => { resultApi.quizStatus(lesson.id).then(setQuizStatus).catch(() => { /* noop */ }) }
    load()
    const onVis = () => { if (!document.hidden) load() }
    window.addEventListener('focus', load)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('focus', load)
      document.removeEventListener('visibilitychange', onVis)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id])

  useEffect(() => {
    if (!lesson?.id) return
    channelApi.questions(lesson.id).then(setQuestions).catch(() => setQuestions([]))
  }, [lesson?.id])

  const isLessonOwner = !!lesson && !!user && (user.role === 'admin' || user.id === lesson.ownerId)

  const fmtDateTime = (value?: string | null) => {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const handleAskQuestion = async () => {
    if (!lesson || postingQuestion) return
    const body = questionText.trim()
    if (body.length < 5) { alert('Câu hỏi cần ít nhất 5 ký tự.'); return }
    setPostingQuestion(true)
    try {
      const created = await channelApi.askQuestion(lesson.id, body)
      setQuestions((prev) => [created, ...prev])
      setQuestionText('')
    } catch {
      alert('Chưa gửi được câu hỏi. Hãy đăng nhập rồi thử lại.')
    } finally {
      setPostingQuestion(false)
    }
  }

  const handleAnswerQuestion = async (id: string) => {
    const answer = (answerDrafts[id] || '').trim()
    if (answer.length < 2) { alert('Câu trả lời quá ngắn.'); return }
    try {
      const updated = await channelApi.answerQuestion(id, answer, true)
      setQuestions((prev) => prev.map((q) => q.id === id ? updated : q))
      setAnswerDrafts((prev) => ({ ...prev, [id]: '' }))
    } catch {
      alert('Chưa lưu được câu trả lời.')
    }
  }

  const handleHideQuestion = async (id: string) => {
    if (!confirm('Ẩn câu hỏi này?')) return
    try {
      await channelApi.hideQuestion(id)
      setQuestions((prev) => prev.filter((q) => q.id !== id))
    } catch {
      alert('Chưa ẩn được câu hỏi.')
    }
  }
  if (lesson === undefined) {
    return <div className="p-10 text-center text-gray-400">Đang tải...</div>
  }
  if (!lesson) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500 mb-4">Không tìm thấy bài giảng.</p>
        <Link to="/teacher/elearning" className="text-blue-600 font-semibold">← Về danh sách bài giảng</Link>
      </div>
    )
  }

  const cur = sections[active]
  const curMeta = cur ? sectionKindMeta(cur) : null
  const liveStats = statsKey ? LessonStatsService.getStats(statsKey) : null

  // Tỉ lệ hoàn thành 1 phần ngay tại thời điểm hiện tại (dùng memo tổng mục → chính xác từ lần render đầu).
  const liveRatio = (i: number): number => {
    const b = (statsKey ? LessonStatsService.getStats(statsKey) : null)?.sections?.[i]
    const tT = textTotals[i] || 0, qT = gateTotals[i] || 0, vT = videoTotals[i] || 0, zT = quizTotals[i] || 0
    const total = tT + qT + vT + zT
    if (total === 0) return visited.has(i) ? 100 : 0
    const tD = Math.min(tT, b?.textViewed?.size || 0)
    const qD = Math.min(qT, (b?.correct || 0) + (b?.wrong || 0))
    const doneKeys = statsKey ? LessonStatsService.getAnsweredKeys(statsKey) : new Set<string>()
    const vD = Math.min(vT, (interactiveVideoIds[i] || []).filter((id) => doneKeys.has(cardDoneKey(id))).length)
    const zD = Math.min(zT, quizDone[i] || 0)
    return Math.round(((tD + qD + vD + zD) / total) * 100)
  }

  // Một mục trong thanh điều hướng các phần.
  const progress = sections.length
    ? Math.round((sections.reduce((n, _s, i) => n + (liveRatio(i) >= 100 ? 1 : 0), 0) / sections.length) * 100)
    : 0

  const NavItem = ({ i }: { i: number }) => {
    const s = sections[i]
    const m = sectionKindMeta(s)
    const on = i === active
    const ratio = liveRatio(i)
    const done = ratio >= 100
    return (
      <button
        onClick={() => goto(i)}
        className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all flex items-center gap-2.5 ${
          on ? 'bg-white shadow-sm border-transparent ring-2' : 'bg-transparent border-transparent hover:bg-white/70'
        }`}
        style={on ? ({ ['--tw-ring-color' as any]: m.accent } as React.CSSProperties) : undefined}
      >
        <span
          className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-base"
          style={{ background: `${m.accent}1a` }}
        >
          {m.icon}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[11px] font-bold uppercase tracking-wide" style={{ color: m.accent }}>
            {m.label}
          </span>
          <span className={`block text-sm font-semibold truncate ${on ? 'text-gray-900' : 'text-gray-600'}`}>
            {s.title}
          </span>
        </span>
        {done ? (
          <Check size={16} className="text-emerald-500 shrink-0" />
        ) : ratio > 0 ? (
          <span className="text-[10px] font-bold text-gray-400 shrink-0 tabular-nums">{ratio}%</span>
        ) : null}
      </button>
    )
  }

  return (
    <LessonDocProvider lesson={lesson}>
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-20">
        <div className={`${navOpen ? 'max-w-[1280px]' : 'max-w-[1600px]'} mx-auto flex items-center gap-3`}>
          <Link to="/teacher/elearning" className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </Link>
          {sections.length > 0 && (
            <button
              onClick={() => setNavOpen((v) => !v)}
              title={navOpen ? 'Ẩn danh sách phần' : 'Hiện danh sách phần'}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              {navOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </button>
          )}
          <h1 className="text-lg font-black text-gray-900 truncate">{lesson.meta.tieuDe}</h1>
          <span className="ml-auto text-xs font-bold text-gray-400 whitespace-nowrap">
            {sections.length ? `${active + 1}/${sections.length} phần` : ''}
          </span>
          <button
            onClick={toggleFullscreen}
            title={isFs ? 'Thoát toàn màn hình' : 'Toàn màn hình (giảng dạy)'}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
          >
            {isFs ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>
        {/* Thanh tiến độ */}
        {sections.length > 0 && (
          <div className={`${navOpen ? 'max-w-[1280px]' : 'max-w-[1600px]'} mx-auto mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden`}>
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      {sections.length === 0 ? (
        <div className="p-16 text-center text-gray-400">Bài giảng chưa có nội dung.</div>
      ) : (
        <div className={`${navOpen ? 'max-w-[1280px]' : 'max-w-[1600px]'} w-full mx-auto flex-1 flex flex-col md:flex-row gap-6 p-4 md:p-6`}>
          {/* Tab điều hướng — dọc trên md+; mobile: các "chip" xuống hàng để THẤY HẾT các phần cần học */}
          {navOpen && (
            <nav className="md:w-64 md:shrink-0">
              {/* Mobile (chế độ dọc): panel nền rõ + dòng hướng dẫn; chip tự xuống hàng (không cuộn ngang) */}
              <div className="md:hidden mb-2 rounded-xl border border-blue-100 bg-blue-50/60 p-3">
                <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase tracking-wide text-blue-700">
                  <span aria-hidden>📚</span>
                  <span>Các phần cần học ({sections.length}) · chạm để chuyển</span>
                </div>
                <div className="flex flex-wrap gap-2">
                {sections.map((s, i) => {
                  const m = sectionKindMeta(s)
                  const on = i === active
                  const ratio = liveRatio(i)
                  const done = ratio >= 100
                  return (
                    <button
                      key={i}
                      onClick={() => goto(i)}
                      className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-full border text-sm font-bold transition ${
                        on ? 'text-white shadow-sm' : 'bg-white text-gray-700 border-gray-200'
                      }`}
                      style={on ? { background: m.accent, borderColor: m.accent } : undefined}
                    >
                      <span className="text-base leading-none">{m.icon}</span>
                      <span>{i + 1}. {m.label}</span>
                      {done ? (
                        <Check size={14} className={on ? 'text-white' : 'text-emerald-500'} />
                      ) : ratio > 0 ? (
                        <span className="tabular-nums opacity-80 text-xs">{ratio}%</span>
                      ) : null}
                    </button>
                  )
                })}
                </div>
              </div>
              {/* Desktop: sidebar dọc như cũ */}
              <div className="hidden md:block md:sticky md:top-24 space-y-1.5">
                {sections.map((_, i) => (
                  <NavItem key={i} i={i} />
                ))}
              </div>
            </nav>
          )}

          {/* Khu nội dung rộng — chỉ hiện phần đang chọn.
              overflowAnchor:none → tắt scroll-anchoring để ảnh/iframe mô phỏng tải xong KHÔNG kéo trang nhảy. */}
          <main className="flex-1 min-w-0" style={{ overflowAnchor: 'none' }}>
            {/* Nhiệm vụ NHÓM gắn vào bài này → dẫn sang Cộng tác nhóm */}
            {groupTasks.length > 0 && (
              <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                <div className="flex items-center gap-2 font-black text-indigo-800 text-sm mb-1.5">
                  <Users size={16} /> Nhiệm vụ nhóm của bài này ({groupTasks.length})
                </div>
                <div className="space-y-1">
                  {groupTasks.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center gap-2 text-sm text-indigo-900">
                      <span className="font-semibold">{t.title}</span>
                      {t.due_date && <span className="text-xs text-indigo-600">· Hạn {new Date(t.due_date).toLocaleDateString('vi-VN')}</span>}
                      <Link to="/cong-tac" className="ml-auto text-xs font-bold text-indigo-700 hover:underline whitespace-nowrap">Làm việc nhóm & nộp sản phẩm →</Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!restored && (
              <div className="py-20 text-center text-gray-400">Đang tải tiến độ học…</div>
            )}
            {restored && cur && curMeta && (
              <section>
                {/* Tiêu đề phần */}
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{ background: `${curMeta.accent}1a` }}
                  >
                    {curMeta.icon}
                  </span>
                  <div>
                    <span className={`inline-block text-[11px] font-black uppercase tracking-wide px-2 py-0.5 rounded-md border ${curMeta.chip}`}>
                      {curMeta.label}
                    </span>
                    <h2 className="text-xl md:text-2xl font-black text-gray-900 leading-tight">{cur.title}</h2>
                  </div>
                </div>

                {/* Lồng tiếng phần Khởi động (nếu GV đã tạo) */}
                {curMeta.kind === 'warmup' && cur.audioUrl && (
                  <div className="mb-4 rounded-xl border border-orange-100 bg-orange-50/60 p-3 flex items-center gap-3">
                    <span className="text-lg" aria-hidden>🎙️</span>
                    <span className="text-sm font-bold text-orange-800 shrink-0">Nghe dẫn nhập</span>
                    <audio controls preload="none" src={cur.audioUrl} className="flex-1 min-w-0 h-9" />
                  </div>
                )}

                {/* Các thẻ trong phần — phần "Hình thành kiến thức" hiển thị dạng lộ trình vượt ải */}
                {curMeta.kind === 'content' && cur.cards.length > 0 ? (
                  <QuestSection
                    cards={cur.cards}
                    statsKey={statsKey}
                    sectionIndex={active}
                    sectionTitle={cur.title}
                    lessonSlug={lesson.slug || lesson.id}
                    accent={curMeta.accent}
                    bypassLock={bypassLock}
                  />
                ) : (
                  <div className="space-y-4">
                    {cur.cards.map((card: any) => {
                      const qz = card.type === 'quiz' && card.bankRef ? quizStatus[card.bankRef] : undefined
                      return (
                        <div key={card.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6">
                          <LessonCardRenderer
                            card={card}
                            onComplete={isInteractiveVideoCard(card) ? () => completeStandaloneCard(card) : undefined}
                          />
                          {card.type === 'quiz' && card.bankRef && (
                            <div className="mt-3 text-xs font-bold">
                              {qz?.done ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <Check size={13} /> Đã làm trắc nghiệm{qz.percent != null ? ` · ${qz.percent}%` : ''}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  Chưa làm — bấm “Làm bài” để hoàn thành phần này
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Điều hướng Phần trước / sau */}
                <div className="flex items-center justify-between gap-3 mt-8">
                  <button
                    onClick={() => goto(active - 1)}
                    disabled={active === 0}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={18} /> Phần trước
                  </button>
                  {active < sections.length - 1 ? (
                    <button
                      onClick={() => goto(active + 1)}
                      className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-black text-white shadow-md hover:brightness-110"
                      style={{ background: curMeta.accent }}
                    >
                      Phần tiếp theo <ChevronRight size={18} />
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-black bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Check size={18} /> Hoàn thành bài giảng
                    </span>
                  )}
                </div>

                {/* Tổng kết học tập (phần cuối) */}
                {active === sections.length - 1 && (
                  <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <h3 className="text-base font-black text-emerald-800 mb-3 flex items-center gap-2">
                      <Check size={18} /> Tổng kết học tập
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { n: `${Math.min(100, Math.round(progress))}%`, t: 'Nội dung đã xem' },
                        { n: `${Math.min(visited.size, sections.length)}/${sections.length}`, t: 'Phần đã xem' },
                        { n: liveStats ? `${liveStats.correctAnswers}/${liveStats.correctAnswers + liveStats.wrongAnswers}` : '0/0', t: 'Câu trả lời đúng' },
                        { n: liveStats?.aiQuestionsAsked ?? 0, t: 'Lần hỏi AI' },
                      ].map((s, i) => (
                        <div key={i} className="bg-white rounded-xl border border-emerald-100 p-3 text-center">
                          <div className="text-xl font-black text-emerald-700">{s.n}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{s.t}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Kết quả được lưu về giáo viên khi bạn rời bài học.</p>
                  </div>
                )}
              </section>
            )}
            {(questions.length > 0 || user) && (
              <section className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MessageCircle size={20} className="text-blue-600" />
                  <h2 className="text-lg font-black text-gray-900">Hỏi đáp bài học</h2>
                  <span className="ml-auto text-xs font-bold text-gray-400">{questions.length} câu hỏi</span>
                </div>

                {user ? (
                  <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
                    <textarea
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      placeholder="Đặt câu hỏi cho giáo viên về nội dung bài học này..."
                      rows={3}
                      className="w-full px-3 py-2 rounded-xl border border-blue-100 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-y"
                    />
                    <div className="mt-2 flex items-center gap-3">
                      <p className="text-xs text-blue-700">Câu hỏi sẽ hiển thị trong kênh chính thức của bài học.</p>
                      <button
                        onClick={handleAskQuestion}
                        disabled={postingQuestion}
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-black hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Send size={15} /> {postingQuestion ? 'Đang gửi...' : 'Gửi câu hỏi'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mb-4 text-sm text-gray-400">Đăng nhập để đặt câu hỏi cho giáo viên.</p>
                )}

                {questions.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Chưa có câu hỏi nào cho bài học này.</p>
                ) : (
                  <div className="space-y-3">
                    {questions.map((q) => (
                      <article key={q.id} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
                        <div className="flex items-start gap-3">
                          <span className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center font-black shrink-0">
                            {(q.author_name || 'H').charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900">{q.author_name || 'Học viên'}</span>
                              <span className="text-[11px] text-gray-400">{fmtDateTime(q.created_at)}</span>
                              {q.is_resolved && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold">Đã trả lời</span>}
                            </div>
                            <p className="mt-1 text-sm text-gray-700 whitespace-pre-line leading-relaxed">{q.body}</p>

                            {q.answer && (
                              <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/70 p-3">
                                <div className="text-xs font-black text-emerald-700 mb-1">Giáo viên trả lời · {fmtDateTime(q.answered_at)}</div>
                                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{q.answer}</p>
                              </div>
                            )}

                            {isLessonOwner && !q.answer && (
                              <div className="mt-3 flex flex-col gap-2">
                                <textarea
                                  value={answerDrafts[q.id] || ''}
                                  onChange={(e) => setAnswerDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                  placeholder="Trả lời chính thức cho học sinh..."
                                  rows={2}
                                  className="w-full px-3 py-2 rounded-xl border border-emerald-100 bg-white text-sm outline-none focus:ring-2 focus:ring-emerald-200 resize-y"
                                />
                                <button onClick={() => handleAnswerQuestion(q.id)} className="self-end inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-black hover:bg-emerald-700">
                                  <Send size={15} /> Trả lời
                                </button>
                              </div>
                            )}
                          </div>
                          {(isLessonOwner || q.author_id === user?.id) && (
                            <button onClick={() => handleHideQuestion(q.id)} title="Ẩn câu hỏi" className="p-2 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}
          </main>
        </div>
      )}
    </div>
    </LessonDocProvider>
  )
}

export default LessonViewerPage
